import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "@/data/site";
import { homeSectionTo } from "@/utils/navigation";

const links = [
  { label: "Usluge", href: "#services" },
  { label: "Kako radimo", href: "#how-it-works" },
  { label: "Za biznis", href: "#fur-specialty" },
  { label: "Iskustva", href: "#testimonials" },
  { label: "Kontakt", href: "#contact" },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 safe-top ${
        scrolled || open
          ? "backdrop-blur-2xl bg-[#030812]/90 border-b border-[#55d0ff]/15"
          : "bg-transparent"
      }`}
    >
      <div className="tw-container flex items-center justify-between h-16 sm:h-[4.5rem] md:h-20 lg:h-[5.25rem]">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2 sm:gap-3 select-none shrink-0"
        >
          <img
            src={SITE.logo}
            alt="TOP WASH DOO — profesionalna B2B perionica Beograd"
            className="h-12 sm:h-14 md:h-16 lg:h-[4.25rem] w-auto max-w-[180px] sm:max-w-[220px] lg:max-w-none drop-shadow-[0_4px_24px_rgba(31,168,255,0.6)]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <Link
              key={l.href}
              to={location.pathname === "/" ? l.href : homeSectionTo(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and").replace(/[čćšđž]/g, (c) => ({ č: "c", ć: "c", š: "s", đ: "d", ž: "z" }[c]))}`}
              className="tw-link font-sans-tw text-[13px] font-medium tracking-wide"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 xl:gap-4 shrink-0">
          <a
            href={SITE.phoneHref}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-[#a7e2ff] text-sm font-semibold hover:text-[#55d0ff] transition whitespace-nowrap"
          >
            <Phone size={16} strokeWidth={2} />
            <span className="hidden xl:inline">{SITE.phone}</span>
          </a>
          <Link
            to={location.pathname === "/" ? "#contact" : homeSectionTo("#contact")}
            className="tw-btn text-[0.7rem] xl:text-[0.78rem] px-4 xl:px-7"
            data-testid="nav-book-button"
          >
            Zakažite procenu
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 -mr-1 text-white touch-target"
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Zatvori meni" : "Otvori meni"}
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden fixed inset-x-0 top-14 sm:top-16 bottom-0 bg-[#030812]/98 backdrop-blur-xl border-t border-[#55d0ff]/15 overflow-y-auto"
          data-testid="nav-mobile-menu"
        >
          <div className="tw-container py-6 flex flex-col gap-4 min-h-full">
            {links.map((l) => (
              <Link
                key={l.href}
                to={location.pathname === "/" ? l.href : homeSectionTo(l.href)}
                onClick={() => setOpen(false)}
                className="font-heading-md text-xl sm:text-2xl text-white py-2 border-b border-[#55d0ff]/10"
                data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
              >
                {l.label}
              </Link>
            ))}
            <a
              href={SITE.phoneHref}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 text-[#a7e2ff] text-base font-semibold py-3"
            >
              <Phone size={18} strokeWidth={2} />
              {SITE.phone}
            </a>
            <Link
              to={location.pathname === "/" ? "#contact" : homeSectionTo("#contact")}
              onClick={() => setOpen(false)}
              className="tw-btn w-full justify-center mt-2"
              data-testid="nav-mobile-book-button"
            >
              Zakažite procenu
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
